import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import { FileText, Download, Info, Calendar } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';

function formatCentsToDollars(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AccountingPage() {
  const navigate = useNavigate();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const now = new Date();
  const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState(currentPeriod);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiRequest(`/api/v1/guides/accounting/tax-summary?period=${period}`, { method: 'GET' });
      if (res.success) {
        setSummary(res.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load tax summary');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await apiRequest(`/api/v1/guides/accounting/tax-export?period=${period}`, { method: 'GET' });
      if (res.success && res.data.csv) {
        const blob = new Blob([res.data.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = res.data.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const months = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    months.push({ value: val, label });
  }

  return (
    <DashboardLayout userRole="guide" activeNavId="accounting" onNavigate={handleNavigate}>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Accounting</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              >
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Download CSV'}
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            Sales tax collected and remitted by OutdoorGuides on your behalf.
            This section shows tax collected on your bookings for your records.
          </p>
        </div>

        {error && <Alert type="error" message={error} className="mb-4" />}

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-sm text-gray-500 mb-1">Tax Collected This Period</p>
              <p className="text-3xl font-bold text-gray-900">
                {summary ? formatCentsToDollars(summary.period_total_cents) : '$0.00'}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-sm text-gray-500 mb-1">Year to Date</p>
              <p className="text-3xl font-bold text-gray-900">
                {summary ? formatCentsToDollars(summary.ytd_total_cents) : '$0.00'}
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
